import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ChangePasswordDialogData, ChangePasswordForm } from '@plantefeve/common/models/users.model';
import { passwordValidator } from '@plantefeve/common/shared/services/password-validator.service';
import { ToastService } from '@plantefeve/common/shared/services/toast.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FluidModule } from 'primeng/fluid';
import { PasswordModule } from 'primeng/password';

@Component({
  selector: 'igx-change-password-dialog',
  imports: [PasswordModule, FloatLabelModule, FormsModule, ReactiveFormsModule, FluidModule, TranslatePipe],
  templateUrl: './change-password-dialog.component.html',
  styleUrl: './change-password-dialog.component.scss',
})
export class ChangePasswordDialogComponent implements OnInit {
  private readonly _dialogConfig = inject(DynamicDialogConfig);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _dialogData = this._dialogConfig.data as ChangePasswordDialogData;
  private readonly _toastService = inject(ToastService);

  public changePasswordForm: FormGroup<ChangePasswordForm> = this._fb.group<ChangePasswordForm>(
    {
      password: new FormControl<string | null>(null, [Validators.required, passwordValidator()]),
      passwordRepeat: new FormControl<string | null>(null, [Validators.required]),
    },
    { validators: this.passwordMatchValidator() }
  );

  ngOnInit(): void {
    this._dialogData.onSubmit = () => this.handleSubmit();
    this._dialogData.onCancel = () => this.handleCancel();
  }

  public handleSubmit(): void {
    if (this.changePasswordForm.invalid) {
      if (this.changePasswordForm.controls.password.hasError('passwordWeak')) {
        this._toastService.showError('Kies een sterker wachtwoord', 'Validatiefout');
      }
      if (this.changePasswordForm.controls.password.hasError('passwordTooShort')) {
        this._toastService.showError('Wachtwoord is te kort', 'Validatiefout');
      }
      if (this.changePasswordForm.controls.password.hasError('passwordMismatch')) {
        this._toastService.showError('Wachtwoorden komen niet overeen.', 'Validatiefout');
      }

      return;
    }

    this._dialogRef.close(this.changePasswordForm.controls.password.value);
  }

  public handleCancel(): void {
    this._dialogRef.close(false);
  }

  private passwordMatchValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const password = group.get('password');
      const passwordRepeat = group.get('passwordRepeat');

      if (!password || !passwordRepeat) {
        return null;
      }

      if (passwordRepeat.errors && !passwordRepeat.errors['passwordMismatch']) {
        return null;
      }

      if (password.value !== passwordRepeat.value) {
        passwordRepeat.setErrors({ passwordMismatch: true });
      } else {
        passwordRepeat.setErrors(null);
      }

      return null;
    };
  }

  public hasMinLength(): boolean {
    const password = this.changePasswordForm.controls.password.value;
    return password ? password.length >= 8 : false;
  }

  public hasUpperCase(): boolean {
    const password = this.changePasswordForm.controls.password.value;
    return password ? /[A-Z]/.test(password) : false;
  }

  public hasNumber(): boolean {
    const password = this.changePasswordForm.controls.password.value;
    return password ? /[0-9]/.test(password) : false;
  }

  public hasSpecialChar(): boolean {
    const password = this.changePasswordForm.controls.password.value;
    return password ? /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) : false;
  }
}
